import {
  Node,
  SortedSide,
  SortedSideCreatePayload,
  SortedSideSortingStrategy,
  SpatialEntity,
  Template,
} from '@ekkogmbh/apisdk';
import { CircularProgress, debounce, ListItemText, MenuItem, Paper, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { convertCoordinateToPath } from '../../Common/Helper/Nodes';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { CenteredColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { TemplatePicker } from 'src/TemplateManagement/TemplatePicker';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import useRequest from 'src/Common/Hooks/useRequest';
import { SortableList } from './Sortables';
import { arrayMoveImmutable } from 'array-move';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';

interface Props {
  sortedSide?: SortedSide;
  closeHandler: () => void;
}
export const SortedSidePanel = ({ sortedSide, closeHandler }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { refresh } = usePagination();
  const { enableSave } = usePanel();

  const [isFetching, startFetching] = useRequest<SpatialEntity[]>();
  const [isFetchingNode, startFetchingNode] = useRequest<Node>();

  const [nodeId, setNodeId] = useState<number>();
  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [key, setKey] = useState<string>('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [sortingStrategy, setSortingStrategy] = useState<SortedSideSortingStrategy>(
    SortedSideSortingStrategy.ALPHANUMERIC_IDENTIFIER,
  );
  const [sortableSpatialEntities, setSortableSpatialEntities] = useState<SpatialEntity[]>([]);
  const [sortedIds, setSortedIds] = useState<string[]>();

  const savePayload = useMemo((): SortedSideCreatePayload | undefined => {
    if ([coordinate, key].includes('')) {
      return undefined;
    }

    return {
      name: name === '' ? undefined : name,
      node: convertCoordinateToPath(coordinate),
      key,
      templates: templates.map((t) => t.id),
      sortingStrategy,
      manuallySortedSpatialEntityIds: sortedIds,
    };
  }, [name, coordinate, templates, key, sortingStrategy, sortedIds]);

  const save = useCallback(async () => {
    if (savePayload === undefined) {
      return;
    }

    if (sortedSide !== undefined) {
      const updatePayload = {
        ...savePayload,
        id: sortedSide.id,
      };
      await api.updateSortedSide(updatePayload);
    } else {
      await api.addSortedSide(savePayload);
    }

    refresh();
    closeHandler();
  }, [api, savePayload, sortedSide, closeHandler]);

  const reset = useCallback(() => {
    setName(sortedSide?.name ?? '');
    setNodeId(sortedSide?.node.id);
    setCoordinate('');
    setTemplates(sortedSide?.templates ?? []);
    setKey(sortedSide?.key ?? '');
    setSortingStrategy(sortedSide?.sortingStrategy ?? SortedSideSortingStrategy.ALPHANUMERIC_IDENTIFIER);
    setSortedIds(sortedSide?.manuallySortedSpatialEntityIds ?? []);
  }, [sortedSide]);

  const fetchSortables = useCallback(() => {
    startFetching(
      async () => {
        if (sortedSide !== undefined) {
          return await api.getSpatialEntitiesBySortedSide(sortedSide);
        } else {
          if (savePayload === undefined) {
            return [];
          }
          const { node, sortingStrategy, key, templates } = savePayload;

          // templates must not be empty
          if (templates.length === 0) {
            return [];
          }

          // manual strat cannot be requested, use another
          const coercedStrategy =
            sortingStrategy === SortedSideSortingStrategy.MANUALLY
              ? SortedSideSortingStrategy.ALPHANUMERIC_IDENTIFIER
              : sortingStrategy;
          return await api.getSpatialEntitiesByNode(node, key, templates, coercedStrategy);
        }
      },
      (result) => {
        const entities = result ?? [];
        if (savePayload?.sortingStrategy === SortedSideSortingStrategy.MANUALLY) {
          // attach new items to the end, keep local sorting
          const sortedEntities = [];
          for (const sortedId of sortedIds ?? []) {
            const idx = entities.findIndex((se) => se.id === parseInt(sortedId));
            if (idx >= 0) {
              const item = entities.splice(idx, 1)[0];
              sortedEntities.push(item);
            }
          }
          setSortableSpatialEntities(sortedEntities.concat(entities));
        } else {
          setSortableSpatialEntities(entities);
        }
      },
    );
  }, [api, sortedSide, savePayload, sortedIds]);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (nodeId !== undefined) {
      startFetchingNode(
        () => api.getNode(nodeId),
        (node) => {
          if (node !== undefined) {
            setCoordinate(node.coordinate);
          }
        },
      );
    }
  }, [nodeId]);

  useEffect(() => {
    enableSave(savePayload !== undefined);
  }, [savePayload]);

  useEffect(() => {
    if (isFetching || savePayload === undefined) {
      return;
    }

    debounce(fetchSortables, 300)();
  }, [coordinate, key, templates, sortingStrategy]);

  useEffect(() => {
    setSortedIds(sortableSpatialEntities.map((se) => String(se.id)));
  }, [sortableSpatialEntities]);

  return (
    <ContentPanel
      steps={[
        {
          title: 'Sorted Side',
          contentCallback: () => (
            <ReactiveRow>
              <CenteredColumn width={'100%'} spacing={2}>
                <StyledTextField
                  label={'Name (optional)'}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value as string);
                  }}
                />
                {isFetchingNode ? (
                  <CircularProgress size={24} />
                ) : (
                  <CoordinateInput
                    label={sortedSide ? 'Area' : 'Coordinate'}
                    value={coordinate}
                    trailingDelimiter={false}
                    onChange={setCoordinate}
                    disabled={sortedSide !== undefined}
                  />
                )}
                <StyledTextField
                  label={'Key'}
                  value={key}
                  onChange={(e) => {
                    setKey(e.target.value as string);
                  }}
                />
                <TemplatePicker
                  multiple
                  linkableOnly={false}
                  linkedOnly={false}
                  coordinate={coordinate}
                  selected={templates ?? []}
                  onChangeCallback={(selected) => {
                    setTemplates(selected as Template[]);
                  }}
                />
                <StyledSelectField
                  label={'Sorting-Strategy'}
                  value={sortingStrategy}
                  onChange={(e) => {
                    setSortingStrategy(e.target.value as SortedSideSortingStrategy);
                  }}
                >
                  {Object.values(SortedSideSortingStrategy).map((strat, i) => (
                    <MenuItem key={i} value={strat}>
                      <ListItemText primary={strat} />
                    </MenuItem>
                  ))}
                </StyledSelectField>
              </CenteredColumn>
              <CenteredColumn width={'100%'} spacing={0}>
                <StyledFormHeader label="Compartment-Value-Order" />
                <Paper>
                  {sortableSpatialEntities.length === 0 ? (
                    <Typography textAlign={'center'} color={'secondary'} variant={'subtitle2'} style={{ margin: 8 }}>
                      No compartments match the selected parameters.
                    </Typography>
                  ) : (
                    <SortableList
                      lockAxis={'y'}
                      items={sortableSpatialEntities}
                      fieldKey={key}
                      onSortEnd={({ oldIndex, newIndex }) => {
                        setSortableSpatialEntities(arrayMoveImmutable(sortableSpatialEntities, oldIndex, newIndex));
                        if (sortingStrategy !== SortedSideSortingStrategy.MANUALLY) {
                          setSortingStrategy(SortedSideSortingStrategy.MANUALLY);
                        }
                      }}
                    />
                  )}
                </Paper>
              </CenteredColumn>
            </ReactiveRow>
          ),
        },
      ]}
      onReset={reset}
      onSave={save}
      onCancel={closeHandler}
    />
  );
};
